import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  TextField,
  IconButton,
  Autocomplete
} from '@mui/material';
import { Person as PersonIcon, Group as GroupIcon, Close as CloseIcon } from '@mui/icons-material';
import { learningPathService } from '../../services/api';
import { useToast } from '../../context/ToastContext';

const EnrollLearnersDialog = ({ open, onClose, lpathId, instances, fetchLearners }) => {
  const { showToast } = useToast();
  
  const [selectedInstance, setSelectedInstance] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const autocompleteRef = useRef(null);
  
  // Set default instance when dialog opens
  useEffect(() => {
    if (open && instances.length > 0 && !selectedInstance) {
      setSelectedInstance(instances[0].id);
    }
  }, [open, instances, selectedInstance]);
  
  // Search for members (users and cohorts)
  const handleSearch = async (term) => {
    setSearchTerm(term);
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await learningPathService.searchMembers(term, lpathId);
      //setSearchResults(response.data);
      if (Array.isArray(response.data)) {
        setSearchResults(response.data);
      } else if (response.data && Array.isArray(response.data.results)) {
        // If the API returns a nested structure with results array
        setSearchResults(response.data.results.map(result => ({
          id: result.id,
          name: result.text || result.name,
          type: result.type
        })));
      } else {
        // Fallback to empty array if response structure is unexpected
        console.error('Unexpected response structure:', response.data);
        setSearchResults([]);
      }
      // setSearchResults(response.data.results.map(result => ({
      //   id: result.id,
      //   name: result.text,
      //   type: result.type
      // })));
    } catch (error) {
      console.error('Error searching members:', error);
      setSearchResults([])
    }
  };
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        handleSearch(searchTerm);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Handle instance change
  const handleInstanceChange = (event) => {
    setSelectedInstance(event.target.value);
  };
  
  // Handle member selection
  const handleAddMember = (member) => {
    if (!selectedMembers.find(m => m.id === member.id && m.type === member.type)) {
      setSelectedMembers([...selectedMembers, member]);
    }
    setSearchTerm('');
    setTimeout(() => {
      if (autocompleteRef.current) {
        const input = autocompleteRef.current.querySelector('input');
        if (input) {
          input.focus();
        }
      }
    }, 50);
  };

  const handleRemoveMember = (member) => {
    setSelectedMembers(selectedMembers.filter(m => 
      !(m.id === member.id && m.type === member.type)
    ));
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!selectedInstance) {
      showToast('Please select an instance', 'error');
      return;
    }
    
    if (selectedMembers.length === 0) {
      showToast('Please select at least one member to enroll', 'error');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await learningPathService.enrollLearners({
        lpath_id: lpathId,
        instance_id: selectedInstance,
        members: selectedMembers.map(member => `${member.type}_${member.id}`)
      });
      
      if (response.data.status === 'success') {
        showToast(response.data.message, 'success');
        onClose(true);
      } else {
        showToast(response.data.message || 'Failed to enroll learners', 'error');
      }
    } catch (error) {
      console.error('Error enrolling learners:', error);
      showToast(error.response?.data?.message || 'Failed to enroll learners', 'error');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Reset form when dialog closes
  const handleDialogClose = () => {
    setSelectedMembers([]);
    setSearchTerm('');
    setSearchResults([]);
    onClose(false);
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Enroll Learners</Typography>
          <IconButton onClick={handleDialogClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {/* <FormControl fullWidth size="small">
            <InputLabel id="instance-select-label">Select Instance</InputLabel>
            <Select
              labelId="instance-select-label"
              value={selectedInstance}
              onChange={handleInstanceChange}
              label="Select Instance"
              required
            >
              {instances.map((instance) => (
                <MenuItem key={instance.id} value={instance.id}>
                  {instance.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
        
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Select Users & Cohorts
            </Typography>
            {/* <Typography variant="body2" sx={{ mb: 2 }}>
              Select specific users or Cohorts below to enroll in this learning path instance.
            </Typography> */}
            <Autocomplete
              ref={autocompleteRef}
              freeSolo
              options={searchResults}
              getOptionLabel={(option) => option.name || ''}
              inputValue={searchTerm}
              onInputChange={(event, newValue) => handleSearch(newValue)}
              onChange={(event, newValue) => {
                if (newValue && typeof newValue === 'object') {
                  handleAddMember(newValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Enroll users or cohorts"
                  variant="outlined"
                  fullWidth
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body1">
                        {option.name} 
                        <Typography variant="caption" color="text.secondary">
                          {option.type === 'user' ? ' (User)' : ' (Group)'}
                        </Typography>
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              value={null}
              clearOnBlur={false}
              disableClearable={true}
              openOnFocus={true}
            />
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedMembers.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected ({selectedMembers.length}):
                  </Typography>
                    {selectedMembers.map((member) => (
                      <Chip sx={{ mr: 0.5 }}
                        key={`${member.type}_${member.id}`}
                        label={member.name}
                        onDelete={() => handleRemoveMember(member)}
                        color={member.type === 'user' ? 'primary' : 'secondary'}
                        variant="outlined"
                        icon={member.type === 'user' ? <PersonIcon /> : <GroupIcon />}
                      />
                    ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleDialogClose} disabled={submitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={submitting || selectedMembers.length === 0}
        >
           {submitting ? 'Enrolling...' : 'Enroll'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnrollLearnersDialog;